import { z } from 'zod';

export enum ChatContextType {
  Global = 'global',
  KolProfile = 'kol-profile',
  Document = 'document',
  Folder = 'folder',
  Brand = 'brand',
  Agent = 'agent',
  Transcript = 'transcript',
  Project = 'project',
  MessageGeneration = 'message-generation',
}

export enum ChatInstanceItemType {
  Query = 1,
  FileUpload = 2,
  KolSearch = 3,
}

export enum ChatFileUploadStatus {
  Errored = 'errored',
  Initiated = 'initiated',
  Started = 'started',
  Ready = 'ready',
  Uploaded = 'uploaded',
}

export enum ChatHintType {
  AnalysisTags = 'analysis-tags',
  FindQuotes = 'find-quotes',
  Text = 'text',
  Chart = 'chart-quant-analyses',
  Prompt = 'prompt',
  ChatWithSegment = 'chat-with-segment',
}

export enum ChatCitationType {
  Generic = 'generic',
  KolProfile = 'kol-profile',
  Document = 'document',
  Transcript = 'transcript',
  CrmNote = 'crm-note',
  StrategicImperative = 'strategic-imperative',
  Dashboard = 'dashboard',

  ScientificLeaderTopic = 'scientific-leader-topic',
  ClinicalLeaderTopic = 'clinical-leader-topic',
  DigitalLeaderTopic = 'digital-leader-topic',

  Association = 'association',
  Affiliation = 'affiliation',
  Collaboration = 'collaboration',
  CompanySponsorship = 'company-sponsorship',

  ClinicalTrial = 'clinical-trial',
  Conference = 'conference',
  Publication = 'publication',
  CongressAbstract = 'congress-abstract',

  UnmetNeed = 'unmet-need',
  ContactDetail = 'contact-detail',
  Recommendation = 'recommendation',

  DocumentedInterests = 'documented-interests',
  MedicalInsights = 'medical-insights',
  CallCenterInteractions = 'call-center-interactions',
  FbmsInteractions = 'fbms-interactions',

  QuantAnalysis = 'quant_analysis',
  SurveyQuestionResponses = 'survey_question_responses',

  SegmentPersona = 'segment_persona',
}

export enum ChatHistoryMode {
  All = 'all',
  Page = 'page',
}

export enum ChatQueryStatus {
  Error = 'error',
  Loading = 'loading',
  LoadingExtended = 'loading-extended',
  Streaming = 'streaming',
  Complete = 'complete',
  FollowupsAvailable = 'followups-available',
  Cancelled = 'cancelled',
}

export enum ChatResponseType {
  HcpAvailability = 'hcp-availability',
  HcpSummary = 'hcp-summary',
  Markdown = 'markdown',
  HcpSelection = 'hcp-selection',
  MarkdownHtml = 'markdown-html',
  Text = 'raw-text',
  LocationSelection = 'location-selection',
  Chart = 'chart',
  LocationRouting = 'routing-response',
  SchedulingAssistant = 'scheduling-assistant',
  FormWorkflow = 'form-workflow', // Generic form workflow - replaces specific types
  SmartRouting = 'smart-routing',

  // Response types from BIAI
  BIFindQuotes = 'find-quotes-response',
  BIFindQuantAnalyses = 'find-quant-analyses-response',
  BIChartQuantAnalyses = 'chart-quant-analyses-response',

  // Segmentation workflow response types
  SegmentationTagsNeeded = 'segmentation-tags-needed',
  SegmentationQuestionsNeeded = 'segmentation-questions-needed',
  SegmentationComplete = 'segmentation-complete',
  SegmentationCancelled = 'segmentation-cancelled',

  // Segmentation builder workflow response types
  SegmentationUpload = 'segmentation-upload',
  SegmentationConfigure = 'segmentation-configure',
  SegmentationEDA = 'segmentation-eda',
  SegmentationStrategy = 'segmentation-strategy',
  SegmentationRefinement = 'segmentation-refinement',
  SegmentationFeatures = 'segmentation-features',
  SegmentationResults = 'segmentation-results',
  SegmentationExport = 'segmentation-export',

  // Segment chat workflow response types
  SegmentChatNeeded = 'segment-chat-needed',
  SegmentChatCancelled = 'segment-chat-cancelled',
  SegmentChatSelected = 'segment-chat-selected',
  // Message generation response type
  MarketingMessage = 'marketing-message',
}

export enum ChatAttachmentType {
  Table = 'table',
  Chart = 'chart',
  SuggestedActions = 'suggested-actions',
  HCPSearch = 'hcp-search',
  WidgetChart = 'widget-chart',
  WidgetText = 'widget-text',
  WidgetMetricCard = 'widget-metric-card',
  WidgetRepDashboard = 'widget-rep-dashboard',
  MessageTestingGrid = 'message-testing-grid',
  GeneratedMessages = 'generated-messages',
}

export enum ChatActionType {
  ReaskWithHcp = 'reask-with-hcp',
  SelectLocation = 'select-location',
  BuildRoute = 'build-route',
  SmartRouting = 'smart-routing',
  QueryFiles = 'query-files',
  FindQuotes = 'find-quotes',
  QuerySurveyQuestion = 'query-survey-question',
  SubmitFormWorkflow = 'submit-form-workflow', // Generic form workflow action
  SummarizeFolder = 'summarize-folder',
  LoadDraft = 'load-draft', // Load an existing form draft
  FindQuantAnalyses = 'find-quant-analyses',
  ChartQuantAnalyses = 'chart-quant-analyses',
  AnswerQuantAnalyses = 'answer-quant-analyses',
  StartSegmentAnalysis = 'start-segment-analysis',
  StartSegmentationBuilder = 'start-segmentation-builder',
  ChatWithSegment = 'chat-with-segment',
  SelectSegmentForChat = 'select-segment-for-chat',
  InitiateFormWorkflow = 'initiate-form-workflow',
}
export const ChatActionTypeModel = z.enum(ChatActionType);

export const DisplayModeSchema = z.enum(['compact', 'full']);

export enum CopilotConfigAction {
  FormEntry = 'dispatch:form-entry',
  ProfileRecommendations = 'dispatch:profile-recommendations',
  Workspaces = 'navigate:workspaces',
  QuestionsBank = 'dispatch:questions-bank',
}

export enum CopilotConfigIcon {
  Schedule = 'schedule',
  FormEntry = 'form-entry',
  Insights = 'insights',
  ProfileRecommendations = 'profile-recommendations',
  Workspaces = 'workspaces',
  QuestionsBank = 'questions-bank',
  QuestionsBankAlt = 'questions-bank-alt',
}

export enum BiChatContextType {
  Global = 'bi-global',
  Brand = 'bi-brand',
  Document = 'bi-document',
  Folder = 'bi-folder',
  Project = 'bi-project',
  Transcript = 'bi-transcript',
}

export const BrandInsightChatContexts: string[] = [
  BiChatContextType.Brand,
  BiChatContextType.Document,
  BiChatContextType.Folder,
  BiChatContextType.Project,
  BiChatContextType.Transcript,
  BiChatContextType.Global,
];

export enum EngagementType {
  Proactive = 'Proactive',
  Reactive = 'Reactive',
  None = 'None',
}

export enum ChatPlatform {
  BrandInsights = 'brand-insights',
  GenAIPlatform = 'genai-platform',
}

export type ChatInteractionMode = 'text' | 'voice' | 'video';
